import { queryOptions } from '@tanstack/react-query';

import { gqlFetcher, graphql } from '#features/api';
import { ResultOf } from '#features/api/utils/graphql';
import { noteOutcome } from '#features/store-review/model/earnedOutcome';

const pendingSurveyDoc = graphql(`
  query PendingSessionSurvey {
    session {
      survey {
        sessionId
        postId
        gameId
        startedAt
      }
    }
  }
`);

export type PendingSessionSurvey = NonNullable<
  ResultOf<typeof pendingSurveyDoc>['session']['survey']
>;

export const answerSurveyDoc = graphql(`
  mutation AnswerSessionSurvey($input: SessionSurveyAnswerInput!) {
    session {
      answerSurvey(input: $input) {
        __typename
        ... on Operation {
          status
        }
        ... on GlobalError {
          errorCode
          errorMessage
        }
      }
    }
  }
`);

export type SessionSurveyAnswer = ReturnType<typeof graphql.scalar<'SessionSurveyAnswer'>>;

export const answerSurvey = (sessionId: string, answer: SessionSurveyAnswer) =>
  gqlFetcher(answerSurveyDoc, { input: { sessionId, answer } }).then((data) => {
    const result = data.session.answerSurvey;

    // «Да, сыграли» — единственный ответ, означающий, что сбор дошёл до игры. Остальные три
    // говорят либо о развалившемся сборе, либо о том, что исход ещё неизвестен: просить
    // оценку по такому поводу значило бы догонять человека ровно после разочарования.
    if (answer === 'PLAYED' && result?.__typename === 'Operation') {
      noteOutcome('session');
    }

    return result;
  });

/**
 * Запрос выглядит как чтение, но на сервере он записывает показ карточки — поэтому он не должен
 * повторяться сам. staleTime: Infinity плюс отключённые перезапросы делают его ровно одним на
 * заход: иначе переключение вкладки сожгло бы оба положенных показа за минуту.
 */
export const pendingSessionSurveyQueryOptions = (enabled: boolean) =>
  queryOptions({
    queryKey: ['sessionSurvey', 'pending'],
    queryFn: () => gqlFetcher(pendingSurveyDoc).then((data) => data.session.survey ?? null),
    enabled,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
  });
